"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { searchMyCommands, type PaletteCard } from "@/lib/actions/workshop";
import { CommandCardView } from "@/components/CommandCardView";

/**
 * Command Palette (Workshop W2): ⌘K / Ctrl+K → search command cards across
 * your library (books you authored or saved) → fill placeholders → copy.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PaletteCard[] | null>(null);
  const [active, setActive] = useState<PaletteCard | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setActive(null);
    setQ("");
    setResults(null);
    setSearchFailed(false);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        setActive(null);
      } else if (e.key === "Escape") {
        close();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  const runSearch = useCallback(async (term: string) => {
    setSearching(true);
    try {
      setResults(await searchMyCommands(term));
      setSearchFailed(false);
    } catch {
      // A failed search is not "no matches" — surface it as an error.
      setResults(null);
      setSearchFailed(true);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    // Initial (empty-query) load: most recently updated cards. Deferred a
    // tick so the effect itself doesn't set state synchronously.
    const t = setTimeout(() => void runSearch(""), 0);
    return () => clearTimeout(t);
  }, [open, runSearch]);

  function onInput(v: string) {
    setQ(v);
    setActive(null);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void runSearch(v), 250);
  }

  // Focus trap: Tab cycles within the dialog instead of escaping to the page.
  function trapTab(e: React.KeyboardEvent) {
    if (e.key !== "Tab") return;
    const root = panelRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/35 px-4 pt-[12vh] backdrop-blur-[2px]"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        ref={panelRef}
        className="w-full max-w-[620px] overflow-hidden rounded-[16px] border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapTab}
      >
        <div className="flex items-center gap-2.5 border-b border-border-soft px-4 py-3">
          <span className="font-mono text-[13px] font-bold text-copper">⌘</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => onInput(e.target.value)}
            placeholder="Search commands in your library…"
            className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-muted-2"
            aria-label="Search command cards"
          />
          <kbd className="rounded-[5px] border border-border-soft px-1.5 py-0.5 text-[10.5px] text-muted-2">esc</kbd>
        </div>

        <div className="max-h-[56vh] overflow-y-auto p-2.5" aria-live="polite">
          {active ? (
            <div>
              <button
                onClick={() => setActive(null)}
                className="mb-2 text-[12.5px] font-semibold text-muted hover:text-copper"
              >
                ← Back to results
              </button>
              <CommandCardView card={active} compact />
              <div className="mt-2 px-1 text-[12px] text-muted-2">
                From{" "}
                <Link
                  href={`/books/${active.bookSlug}`}
                  onClick={close}
                  className="font-semibold text-green hover:text-copper"
                >
                  {active.bookTitle}
                </Link>
                {active.warnings ? <> · <span className="text-copper">Careful:</span> {active.warnings}</> : null}
              </div>
            </div>
          ) : searchFailed && !searching ? (
            <p className="px-3 py-6 text-center text-[13.5px] font-semibold text-danger" role="alert">
              Search failed — check your connection and type to retry.
            </p>
          ) : results === null || searching ? (
            <p className="px-3 py-6 text-center text-[13.5px] text-muted">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-6 text-center text-[13.5px] leading-relaxed text-muted">
              No command cards {q ? <>matching “{q}”</> : null} in your library yet.
              <br />
              Authors add them in the studio; save books that carry them.
            </p>
          ) : (
            <ul className="grid gap-1">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setActive(c)}
                    className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left hover:bg-paper/70"
                  >
                    <span
                      className="h-6 w-2 shrink-0 rounded-[3px]"
                      style={{ background: c.cloth }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-ink">{c.name}</span>
                      <span className="block truncate font-mono text-[11.5px] text-muted">{c.command || "—"}</span>
                    </span>
                    {(c.destructive || c.privileged) && (
                      <span className="shrink-0 text-[11px] font-bold text-danger">⚠</span>
                    )}
                    <span className="shrink-0 text-[11.5px] text-muted-2">{c.bookTitle}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
